import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';

import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import { API_URL } from '../../../../utils/constants';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import styled from 'styled-components';
import MarginsApi from '~/src/services/api/MarginsApi';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';

export default function MarginEdit(): JSX.Element {
  const { id } = useParams();
  const [itemData, setItemData] = useState({
    countryId: '',
    marginWholesaler: '',
    marginDistributor: '',
    marginRetailer: '',
    vat: '',
    dataArrived: false,
  });
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    let result = null;
    if (id) {
      result = await MarginsApi.update(id, values);
    } else {
      result = await MarginsApi.create(values);
    }
    if (result.status === StatusCodes.OK) {
      history.push(`/margins`);
    }
    setSubmitting(false);
  };

  const getItem = async () => {
    const result = await MarginsApi.getSinglie(id);
    const newItemData = { ...result.data, dataArrived: true };
    setItemData(newItemData);
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, [id]);

  const handleBack = () => {
    history.push('/margins');
  };

  const onDelete = async () => {
    const result = await MarginsApi.delete(id);
    if (result.status === StatusCodes.OK) {
      history.push('/margins');
    }
  };

  return (
    <WhitePad>
      <TitleComponent title={`${id ? 'Edit ' : 'Create '} Margin`} />
      {id && itemData.dataArrived === false ? null : (
        <Formik
          initialValues={{
            countryId: itemData.countryId,
            marginWholesaler: itemData.marginWholesaler,
            marginDistributor: itemData.marginDistributor,
            marginRetailer: itemData.marginRetailer || '',
            vat: itemData.vat || '',
          }}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            saveItem(values, setSubmitting);
          }}
        >
          {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, errors, isValid }) => (
            <FormWrapper>
              <form onSubmit={handleSubmit} autoComplete="off" style={{ width: '600px' }}>
                <FormRow>
                  <AutocompleteComponent
                    url={`${API_URL}/countries/get`}
                    name="countryId"
                    label="Country"
                    initialValue={values.countryId}
                    onChange={setFieldValue}
                    getOptionLabel={(option) => `${option.name}`}
                    queryFields={['name']}
                    errorMessage={errors.countryId}
                  />
                </FormRow>
                <FormRow>
                  <TextInputComponent
                    label="Margin Wholesaler"
                    name="marginWholesaler"
                    value={values.marginWholesaler}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="number"
                    cols={3}
                  />
                  <TextInputComponent
                    name="marginDistributor"
                    label="Margin Distributor"
                    value={values.marginDistributor}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    type="number"
                  />
                  <TextInputComponent
                    name="marginRetailer"
                    label="Margin Retailer"
                    value={values.marginRetailer}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    type="number"
                  />
                  <TextInputComponent
                    name="vat"
                    label="VAT"
                    value={values.vat}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    type="number"
                  />
                </FormRow>
                <ActionWrapper>
                  <FormRow>
                    <PrimaryButton
                      isSubmitting={isSubmitting}
                      type="submit"
                      title={`${id ? 'Save ' : 'Create '}`}
                      disabled={!isValid}
                    />
                    {id && (
                      <BorderlessButton marginLeft="30px" size="sm" type="button" onClick={onDelete} title="Delete" />
                    )}
                  </FormRow>
                </ActionWrapper>
              </form>
            </FormWrapper>
          )}
        </Formik>
      )}
    </WhitePad>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;
